"use client";

/**
 * Durable per-browser anonymous id.
 *
 * Deliberately stored under its OWN localStorage key rather than riding on the
 * auth token: the guest JWT is overwritten the moment someone logs in, which is
 * precisely why pre-login activity has never been attributable. This value
 * survives login, so the server can link "what this browser did before" to the
 * account once the person identifies themselves.
 *
 * It is an opaque random UUID — no device fingerprinting, nothing derived from
 * personal data — and the server treats it as untrusted input used only for
 * correlation, never for authentication.
 */
const STORAGE_KEY = "sukuto_aid";

const uuidv4 = () => {
  // crypto.randomUUID isn't available on older Safari, which matters here
  // because ~90% of traffic is mobile.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const b = crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40; // version 4
    b[8] = (b[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return null;
};

/**
 * Read the existing id, creating one on first use.
 * Returns null when unavailable (SSR, or storage blocked in private mode) —
 * callers must treat the header as optional rather than failing the request.
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const getAnonymousId = () => {
  if (typeof window === "undefined") return null;
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    // Re-validate on every read. The server silently drops a malformed id, so
    // a corrupted stored value would otherwise disable attribution for this
    // browser forever with no signal anywhere — re-mint instead.
    if (existing && UUID_RE.test(existing)) return existing;
    const fresh = uuidv4();
    if (!fresh) return null;
    window.localStorage.setItem(STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // Private mode / storage disabled — degrade to no anonymous id.
    return null;
  }
};

/**
 * Drop the current id so the next request mints a fresh one. Called on logout.
 *
 * Without this, a shared browser — a partner's shop tablet, a family phone —
 * keeps one id across different people, so their sessions accumulate under a
 * single identifier. The server also refuses to link an id to a second customer,
 * but rotating here is what stops the situation arising: each person gets their
 * own id, and no record is ever built of which customers share a device.
 */
export const resetAnonymousId = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — a failure here just means the id persists.
  }
};
